import React from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { 
  WalletIcon, 
  ArrowRightOnRectangleIcon,
  CurrencyDollarIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';

const WalletConnection: React.FC = () => {
  const { 
    wallet, 
    isConnecting, 
    connectWallet, 
    disconnectWallet, 
    tokenBalances, 
    transactionHistory 
  } = useWeb3();

  if (!wallet?.isConnected) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <WalletIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">连接钱包</h3>
          <p className="text-gray-600 mb-6">连接您的 Web3 钱包以使用区块链功能</p>
          
          <button
            onClick={connectWallet}
            disabled={isConnecting}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
          >
            <WalletIcon className="w-5 h-5" />
            {isConnecting ? '连接中...' : '连接钱包'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 钱包信息 */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">钱包信息</h3>
          <button
            onClick={disconnectWallet}
            className="text-red-600 hover:text-red-700 flex items-center gap-2 text-sm"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            断开连接
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">钱包地址</span>
            <p className="font-mono text-sm break-all">{wallet.address}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">网络</span>
            <p className="font-medium">{wallet.network}</p>
          </div>
        </div>
      </div>

      {/* 代币余额 */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4">代币余额</h3>
        {tokenBalances.length === 0 ? (
          <p className="text-gray-500">暂无代币余额</p>
        ) : (
          <div className="space-y-3">
            {tokenBalances.map((token, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium">{token.symbol}</p>
                    <p className="text-sm text-gray-500">${token.usdValue}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{parseFloat(token.balance).toFixed(4)}</p>
                  <p className="text-sm text-gray-500">{token.symbol}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 交易历史 */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold mb-4">最近交易</h3>
        {transactionHistory.length === 0 ? (
          <p className="text-gray-500">暂无交易记录</p>
        ) : (
          <div className="space-y-3">
            {transactionHistory.slice(0, 5).map((tx) => (
              <div key={tx.hash} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ClockIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-sm">
                      {tx.type === 'copyright_register' ? '版权注册' :
                       tx.type === 'nft_mint' ? 'NFT 铸造' :
                       tx.type === 'revenue_claim' ? '收益提取' : '其他交易'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(tx.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    tx.status === 'confirmed' 
                      ? 'bg-green-100 text-green-800'
                      : tx.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {tx.status === 'confirmed' ? '已确认' :
                     tx.status === 'pending' ? '待确认' : '失败'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletConnection;
